import {Fragment, useEffect, useMemo, useState} from 'react';
import {ChevronRight, Search} from 'lucide-react';
import {
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupInput
} from '@kroll-resolver-crisp/ui-components-react';
import type {Concept} from './workflowUi';

type Flags = Record<string, {staged?: boolean; nodef?: boolean; nodata?: boolean}>;

export function ConceptTree({
  nodes,
  selected,
  onPick,
  counts,
  flags
}: {
  readonly nodes: Concept[];
  readonly selected: string;
  readonly onPick: (id: string) => void;
  readonly counts: Record<string, number>;
  readonly flags: Flags;
}) {
  const byId = useMemo(() => new Map(nodes.map((n) => [n.concept_id, n])), [nodes]);
  const kids = useMemo(() => {
    const map = new Map<string, Concept[]>();
    for (const node of nodes) {
      const parent =
        node.parent_concept_id && byId.has(node.parent_concept_id) ? node.parent_concept_id : '';
      map.set(parent, [...(map.get(parent) ?? []), node]);
    }

    return map;
  }, [nodes, byId]);
  const [open, setOpen] = useState(() => new Set((kids.get('') ?? []).map((n) => n.concept_id)));
  const [query, setQuery] = useState('');

  useEffect(() => {
    const ancestors: string[] = [];
    let current = byId.get(selected);
    while (current?.parent_concept_id && byId.has(current.parent_concept_id)) {
      ancestors.push(current.parent_concept_id);
      current = byId.get(current.parent_concept_id);
    }

    if (ancestors.length > 0) {
      setOpen((previous) => new Set([...previous, ...ancestors]));
    }
  }, [selected, byId]);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      return null;
    }

    const shown = new Set<string>();
    for (const node of nodes) {
      if (!node.name.toLowerCase().includes(term)) {
        continue;
      }

      let current: Concept | undefined = node;
      while (current && !shown.has(current.concept_id)) {
        shown.add(current.concept_id);
        current = current.parent_concept_id ? byId.get(current.parent_concept_id) : undefined;
      }
    }

    return shown;
  }, [query, nodes, byId]);

  function toggle(id: string) {
    setOpen((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }

  function row(node: Concept, depth: number) {
    if (visible && !visible.has(node.concept_id)) {
      return null;
    }

    const children = kids.get(node.concept_id) ?? [];
    const expanded = visible ? true : open.has(node.concept_id);
    const flag = flags[node.concept_id] ?? {};
    const active = selected === node.concept_id;
    return (
      <Fragment key={node.concept_id}>
        <div
          role="treeitem"
          aria-selected={active}
          aria-expanded={children.length > 0 ? expanded : undefined}
          className={`hover:bg-muted flex items-center gap-1 pr-2 ${active ? 'bg-accent shadow-[inset_2px_0_0_var(--primary)]' : ''}`}
          style={{paddingLeft: 4 + depth * 14}}>
          {children.length > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground size-5 shrink-0"
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`}
              onClick={() => {
                toggle(node.concept_id);
              }}>
              <ChevronRight aria-hidden className={`size-3 ${expanded ? 'rotate-90' : ''}`} />
            </Button>
          ) : (
            <span className="w-5 shrink-0" />
          )}
          <button
            type="button"
            title={flag.nodata ? 'No grounding: never offered to the model' : undefined}
            className={`flex min-w-0 flex-1 items-center gap-2 py-1.5 pl-1 text-left text-sm ${flag.nodata && !active ? 'opacity-50' : ''}`}
            onClick={() => {
              onPick(node.concept_id);
            }}>
            <span className="min-w-0 flex-1 truncate">{node.name}</span>
            {flag.staged ? (
              <span
                role="img"
                title="Unsent changes"
                aria-label="Unsent changes"
                className="bg-primary size-1.5 shrink-0 rounded-full"
              />
            ) : null}
            {flag.nodef && children.length === 0 ? (
              <span
                role="img"
                title="No definition"
                aria-label="No definition"
                className="ring-muted-foreground size-[7px] shrink-0 rounded-full ring-[1.5px] ring-inset"
              />
            ) : null}
            <span className="text-muted-foreground min-w-4.5 text-right text-xs tabular-nums">
              {counts[node.concept_id] || ''}
            </span>
          </button>
        </div>
        {expanded ? children.map((child) => row(child, depth + 1)) : null}
      </Fragment>
    );
  }

  return (
    <div className="bg-card border-border flex max-h-[calc(100vh-230px)] flex-col overflow-hidden rounded-lg border lg:sticky lg:top-0">
      <div className="border-border border-b p-2">
        <InputGroup>
          <InputGroupAddon>
            <Search aria-hidden className="size-3.5" />
          </InputGroupAddon>
          <InputGroupInput
            value={query}
            placeholder="Filter concepts"
            aria-label="Filter concepts"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </InputGroup>
      </div>
      <div role="tree" aria-label="Concepts in scope" className="min-h-0 flex-1 overflow-auto py-1">
        {(kids.get('') ?? []).map((node) => row(node, 0))}
        {visible?.size === 0 ? (
          <p className="text-muted-foreground m-0 px-3 py-2 text-xs">No concepts match.</p>
        ) : null}
      </div>
      <div className="border-border text-muted-foreground flex gap-3 border-t px-2.5 py-1.5 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="ring-muted-foreground size-[7px] rounded-full ring-[1.5px] ring-inset" />
          No definition
        </span>
        <span className="flex items-center gap-1.5">
          <span className="opacity-50">Faded</span>
          No grounding, not offered
        </span>
        <span className="flex items-center gap-1.5">
          <span className="bg-primary size-1.5 rounded-full" />
          Unsent change
        </span>
      </div>
    </div>
  );
}
